import { emptyIfInstant } from "./mockMode";

/**
 * Mock data for the Sales Orders + Dealers admin module.
 *
 * Shapes mirror the InstantDB schema (sales orders, dealers, dealerTiers,
 * dealerOffers, salesOrderLines, salesOrderStatusHistory). Money is
 * BigInt cents in BDT unless noted. Currency on each row stays as the
 * payment currency code; the FX snapshot is `100_000_000n` (identity)
 * since everything is BDT in this dataset.
 *
 * Dataset:
 *   - 8 dealers across all 4 tiers, BD-flavoured names + addresses.
 *   - 4 dealer tiers (bronze / silver / gold / platinum).
 *   - 12 sales orders covering every status in the lifecycle (draft,
 *     submitted, approved, picking, packed, shipped, delivered, rejected,
 *     cancelled).
 *   - 35 lines (2-4 per order) referencing the catalog mock product set.
 *   - 30 status history rows so the timeline view always renders.
 *   - 15 dealer offers across the 8 dealers + 4 products.
 *
 * Helpers at the bottom let pages look rows up by id without a Map.
 */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

const FX_BDT = 100_000_000n; // identity rate, base currency
const DEV_USER = "dev-user";
// Matches `mockBranches[*]._id` from the inventory mock. Using
// the canonical id keeps the branch column / filter wired without
// per-page lookup tables.
const HQ_BRANCH = "branch_hq";

/* ─────────────────────────────────────────────────────────────
 * Types — narrow to what the UI consumes.
 * ───────────────────────────────────────────────────────────── */

export type SalesOrderStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "picking"
  | "packed"
  | "shipped"
  | "delivered"
  | "cancelled";

export type MockDealerAddress = {
  line1: string;
  line2?: string | null;
  city: string;
  country: string;
};

export type MockDealerContact = {
  phone: string;
  email: string | null;
  contactPerson: string | null;
};

export type MockDealer = {
  _id: string;
  code: string;
  legalName: string | null;
  displayName: string;
  tierId: string;
  branchId: string;
  /** Optional sales territory; see `dealerAreasMock.ts`. */
  dealerAreaId?: string | null;
  primaryUserId: string | null;
  creditLimitCents: bigint;
  creditLimitCurrency: string;
  creditUsedCents: bigint;
  paymentTermsDays: number;
  addressJson: MockDealerAddress;
  contactJson: MockDealerContact;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  /**
   * Opening AR balance carried over when BizOP went live, in BDT base cents.
   * +ve = dealer owes us; −ve = we owe dealer. Default 0n. Optional so legacy
   * mock rows compile without explicit values.
   */
  openingBalanceBaseCents?: bigint;
  /** As-of date for the opening balance; null until set. */
  openingBalanceAsOf?: number | null;
  /** Where this dealer was imported from; null for manually created. */
  importSource?: string | null;
};

export type MockDealerTier = {
  _id: string;
  code: string;
  displayName: string;
  /** Discount in basis points (100 bp = 1%). */
  discountBp: number;
  /** Points multiplier scaled 1e4 (10000 = 1.0x, 20000 = 2.0x). */
  pointsMultiplierE4: bigint;
  sortOrder: number;
  isActive: boolean;
};

export type MockSalesOrder = {
  _id: string;
  orderNumber: string;
  dealerId: string;
  branchId: string;
  placedByUserId: string;
  status: SalesOrderStatus;
  placedAt: number;
  approvedAt: number | null;
  approvedByUserId: string | null;
  shippedAt: number | null;
  deliveredAt: number | null;
  requestedDeliveryDate: number | null;
  currency: string;
  fxRateToBaseE8: bigint;
  amountCents: bigint;
  discountInBaseCents: bigint;
  taxInBaseCents: bigint;
  totalInBaseCents: bigint;
  pointsEarned: bigint;
  pointsRedeemed: bigint;
  notes: string | null;
  createdAt: number;
  updatedAt: number;
};

export type MockSalesOrderLine = {
  _id: string;
  salesOrderId: string;
  productId: string;
  variantId: string | null;
  quantityOrdered: number;
  unitPriceCents: bigint;
  unitId: string;
  currency: string;
  lineTotalInBaseCents: bigint;
  taxRateId: string | null;
  sortOrder: number;
};

export type MockSalesOrderStatusHistory = {
  _id: string;
  salesOrderId: string;
  fromStatus: SalesOrderStatus | null;
  toStatus: SalesOrderStatus;
  changedByUserId: string;
  changedAt: number;
  reason: string | null;
};

export type MockDealerOffer = {
  _id: string;
  dealerId: string;
  productId: string;
  variantId: string | null;
  unitPriceCents: bigint;
  currency: string;
  minQuantity: number;
  validFrom: number;
  validUntil: number | null;
  isActive: boolean;
};

/* ─────────────────────────────────────────────────────────────
 * Tiers (4)
 * ───────────────────────────────────────────────────────────── */

export const mockDealerTiers: MockDealerTier[] = emptyIfInstant<MockDealerTier>([]);

/* ─────────────────────────────────────────────────────────────
 * Dealers (8) — across all 4 tiers, all 8 BD divisions implied.
 * ───────────────────────────────────────────────────────────── */

export const mockDealers: MockDealer[] = emptyIfInstant<MockDealer>([]);

/* ─────────────────────────────────────────────────────────────
 * Sales orders (12) — covers all 9 statuses.
 *
 * Total amounts are realistic 5-150k BDT each. The status spread:
 *   draft × 1, submitted × 2, approved × 1, picking × 1, packed × 1,
 *   shipped × 1, delivered × 3, rejected × 1, cancelled × 1.
 * ───────────────────────────────────────────────────────────── */

export const mockSalesOrders: MockSalesOrder[] = emptyIfInstant<MockSalesOrder>([]);

/* ─────────────────────────────────────────────────────────────
 * Lines (35 total — 2-4 per order). Reference catalogMock products.
 * ───────────────────────────────────────────────────────────── */

export const mockSalesOrderLines: MockSalesOrderLine[] = emptyIfInstant<MockSalesOrderLine>([]);

/* ─────────────────────────────────────────────────────────────
 * Status history (30 rows). Each row records one transition.
 * ───────────────────────────────────────────────────────────── */

export const mockSalesOrderStatusHistory: MockSalesOrderStatusHistory[] = emptyIfInstant<MockSalesOrderStatusHistory>([]);

/* ─────────────────────────────────────────────────────────────
 * Dealer offers (15) — slightly-discounted prices for repeat dealers.
 * ───────────────────────────────────────────────────────────── */

export const mockDealerOffers: MockDealerOffer[] = emptyIfInstant<MockDealerOffer>([]);

/* ─────────────────────────────────────────────────────────────
 * Lookup helpers — small wrappers so pages avoid Map-bookkeeping.
 * ───────────────────────────────────────────────────────────── */

export function findDealer(id: string): MockDealer | null {
  return mockDealers.find((d) => d._id === id) ?? null;
}

export function findOrder(id: string): MockSalesOrder | null {
  return mockSalesOrders.find((o) => o._id === id) ?? null;
}

export function findDealerTier(id: string): MockDealerTier | null {
  return mockDealerTiers.find((t) => t._id === id) ?? null;
}

export function linesForOrder(orderId: string): MockSalesOrderLine[] {
  return mockSalesOrderLines
    .filter((l) => l.salesOrderId === orderId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function historyForOrder(
  orderId: string,
): MockSalesOrderStatusHistory[] {
  return mockSalesOrderStatusHistory
    .filter((h) => h.salesOrderId === orderId)
    .sort((a, b) => a.changedAt - b.changedAt);
}

export function offersForDealer(dealerId: string): MockDealerOffer[] {
  return mockDealerOffers
    .filter((o) => o.dealerId === dealerId)
    .sort((a, b) => b.validFrom - a.validFrom);
}

export function ordersForDealer(dealerId: string): MockSalesOrder[] {
  return mockSalesOrders
    .filter((o) => o.dealerId === dealerId)
    .sort((a, b) => b.placedAt - a.placedAt);
}
